"use client";
import { FC, useState } from "react";
import Image from "next/image";
import WistiaPlayer from "@/components/WistiaPlayer";
import { Card, Media } from "@/types/General";
import Link from "@/components/Link";
import { getDataLinkAttributes } from "@/helpers";
import { Button } from "../../Button";

type ResourceCardProps = {
  type?: string;
  media?: Media;
} & Extract<Card, { type: "Resource" }>;
export const ResourceCard: FC<ResourceCardProps> = (props) => {
  const [videoThumbnailUrl, setVideoThumbnailUrl] = useState<string | null>(
    null,
  );
  const wistiaVideo =
    props.media?.type === "video" ? (
      <WistiaPlayer
        onLoadedMediaData={(event) => {
          const videoThumbnail = event.detail.mediaData.assets.find(
            (asset: { type: string }) => asset.type === "still_image",
          );
          setVideoThumbnailUrl(videoThumbnail?.url + "?image_resize=710x400");
        }}
        uuid={props.media.videoId}
      />
    ) : null;

  const image =
    props.media?.type === "image" && props.media.image ? (
      <Image
        alt={props.title}
        src={props.media.image}
        width={710}
        height={400}
        loader={({ src }) => src}
      />
    ) : null;

  return (
    <div
      data-automation-group="card"
      className="shadow-card border-t-symbol-orange-400-graphic border-t-solid border-x-symbol-silver-300-graphic border-x-symbol-silver-300-graphic border-b-symbol-silver-300-graphic border-b-solid flex break-inside-avoid flex-col gap-4 border-x-1 border-t-2 border-b-1 pt-2 pb-6"
    >
      {props.resourceType && (
        <span
          aria-hidden={true}
          className="text-headline-eyebrow text-body-xs px-3 font-medium"
        >
          {props.resourceType}
        </span>
      )}
      {props.media?.type === "video" &&
        (props.link ? (
          <Link href={props.link.url}>
            {videoThumbnailUrl ? (
              <Image
                alt={props.title + " Video thumbnail"}
                src={videoThumbnailUrl}
                width={710}
                height={400}
                loader={({ src }) => src}
              />
            ) : (
              wistiaVideo
            )}
          </Link>
        ) : videoThumbnailUrl ? (
          <Image
            alt={props.title + " Video thumbnail"}
            src={videoThumbnailUrl}
            width={710}
            height={400}
            loader={({ src }) => src}
          />
        ) : (
          wistiaVideo
        ))}
      {image &&
        (props.link ? <Link href={props.link.url}>{image}</Link> : image)}
      <div className="px-3">
        <span
          role="heading"
          aria-level={4}
          aria-label={`${props.type}: ${props.title}`}
          className="text-subhead-2xs text-headline font-bold"
        >
          {props.title}
        </span>
        <div
          className="text-body-s text-body-primary mt-4 mb-6"
          dangerouslySetInnerHTML={{ __html: props.description }}
        />
        {props.link && (
          <Button
            variant="tertiary"
            className="flex w-fit"
            href={props.link.url}
            data-automation-group="link"
            {...getDataLinkAttributes({
              dld: props.link.dld,
              dlt: props.link.dlt,
            })}
          >
            {props.link.title}
          </Button>
        )}
      </div>
    </div>
  );
};
